import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { URL_ACCOUNT_REGISTRATION, URL_SIGNIN } from "@seek/adv-constants";
import { useTranslations } from "@vocab/react";
import { Box, MenuRenderer, IconLanguage, Text, IconChevron, MenuItemLink, IconTick, Strong, Inline, ButtonLink } from "braid-design-system";
import { analyticsLanguageSelectorEvents, analyticsEvents, analyticsDesktopHeaderEvents } from "../../../EmployerFooter/AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import { useChatContext, testTags } from "../../EmployerHeader.mjs";
import { useEmployerHeaderContext, isPending, isAuthenticated } from "../../../EmployerFooter/EmployerFooter.mjs";
import { getCtaPath } from "../BrandedLogo/BrandedLogo.mjs";
import { translations } from "../../../../EmployerHeader/components/.vocab/index.mjs";
import { AccountDropdown } from "../AccountDropdown/AccountDropdown.mjs";
import { getWindowPathWithoutLanguage, ChatHeaderDesktopLink } from "../ChatHeaderLink/ChatHeaderLink.mjs";
import { siteConfig } from "@seek/melways-sites";
import { CustomHeaderDesktopLink } from "../Navigation/HeaderLink.mjs";
import { root, container, isReady } from "./HeaderActions.css.mjs";
const languageMapping = {
  en: "English",
  id: "Bahasa",
  th: "ภาษาไทย",
  zh: "中文"
};
const getLanguagesForSelector = (site) => {
  const siteLanguages = siteConfig[site].languages;
  const languages = Array.isArray(siteLanguages) ? siteLanguages.filter(isListedLanguage) : [];
  if (isAtLeastTwoLanguages(languages)) {
    return languages;
  }
  return;
};
const listedLanguages = ["en", "id", "th"];
const isListedLanguage = (language) => language ? listedLanguages.includes(language) : false;
const isAtLeastTwoLanguages = (languages) => languages.length > 1;
const LanguageSelector = ({
  language = "en",
  site,
  urlResolver
}) => {
  const { handleInteractionTracking } = useEmployerHeaderContext();
  const languages = getLanguagesForSelector(site);
  if (!languages) {
    return null;
  }
  const strippedPath = getWindowPathWithoutLanguage();
  return /* @__PURE__ */ jsx(Box, { "data-testid": "languageSelector", children: /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      reserveIconSpace: true,
      align: "right",
      offsetSpace: "small",
      width: "small",
      onOpen: () => {
        handleInteractionTracking(analyticsLanguageSelectorEvents.toggleOpen);
      },
      onClose: () => {
        handleInteractionTracking(
          analyticsLanguageSelectorEvents.toggleClosed
        );
      },
      trigger: (triggerProps, { open }) => /* @__PURE__ */ jsxs(
        Box,
        {
          userSelect: "none",
          cursor: "pointer",
          ...triggerProps,
          display: "flex",
          alignItems: "center",
          children: [
            /* @__PURE__ */ jsx(
              IconLanguage,
              {
                size: "small",
                data: { testid: "languageSelectorLabel" }
              }
            ),
            " ",
            /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconChevron, { direction: open ? "up" : "down" }) })
          ]
        }
      ),
      children: languages.map((languageOption) => {
        const href = urlResolver({
          language: languageOption,
          path: strippedPath
        });
        return /* @__PURE__ */ jsx(
          MenuItemLink,
          {
            href,
            data: {
              analytics: analyticsEvents.trackSelectLanguage(
                false,
                languageOption
              )
            },
            icon: languageOption === language && /* @__PURE__ */ jsx(IconTick, { tone: "positive" }),
            onClick: () => {
              handleInteractionTracking({
                ...analyticsLanguageSelectorEvents.languageSelected,
                actionPressed: `language selected - ${languageOption}`
              });
              window.location.href = href;
            },
            children: languageOption === language ? /* @__PURE__ */ jsx(Strong, { children: languageMapping[languageOption] }) : /* @__PURE__ */ jsx(Fragment, { children: languageMapping[languageOption] })
          },
          languageOption
        );
      })
    }
  ) });
};
const HeaderActions = ({
  onToggleButtonClick,
  linkRenderer: LinkRenderer,
  id,
  language,
  site,
  identityIsAuthenticated
}) => {
  const { t } = useTranslations(translations);
  const {
    authenticationStatus,
    shouldDisplayActions,
    shouldDisplayCtaButton,
    urlResolver,
    handleInteractionTracking
  } = useEmployerHeaderContext();
  const registerURL = urlResolver({
    path: URL_ACCOUNT_REGISTRATION
  });
  const signInURL = urlResolver({
    path: URL_SIGNIN
  });
  const ctaURL = urlResolver({
    path: getCtaPath(authenticationStatus)
  });
  const authStatusKnown = !isPending(authenticationStatus);
  const shouldDisplayAccountDropdown = isAuthenticated(authenticationStatus);
  const { shouldDisplayChatLink } = useChatContext();
  return /* @__PURE__ */ jsx(
    Box,
    {
      className: root,
      alignItems: "center",
      justifyContent: "flexEnd",
      flexGrow: 0,
      flexShrink: 0,
      display: "flex",
      children: /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            display: "flex",
            alignItems: "center",
            justifyContent: "flexEnd",
            className: [
              container,
              {
                [isReady]: authStatusKnown
              }
            ],
            "data-test": "header-actions-container",
            children: /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
              shouldDisplayChatLink && /* @__PURE__ */ jsx(
                ChatHeaderDesktopLink,
                {
                  linkRenderer: LinkRenderer,
                  urlResolver
                }
              ),
              /* @__PURE__ */ jsx(
                LanguageSelector,
                {
                  language,
                  site,
                  urlResolver,
                  displayAsCompact: identityIsAuthenticated
                }
              ),
              shouldDisplayActions ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(
                  CustomHeaderDesktopLink,
                  {
                    href: signInURL,
                    "data-analytics": analyticsEvents.TRACK_SIGN_IN,
                    "data-test": testTags.SIGN_IN,
                    linkRenderer: LinkRenderer,
                    analyticsEvent: analyticsDesktopHeaderEvents.signIn,
                    children: t("Sign in")
                  }
                ),
                /* @__PURE__ */ jsx(
                  CustomHeaderDesktopLink,
                  {
                    href: registerURL,
                    "data-analytics": analyticsEvents.TRACK_REGISTRATION,
                    "data-test": testTags.REGISTER,
                    linkRenderer: LinkRenderer,
                    analyticsEvent: analyticsDesktopHeaderEvents.register,
                    children: t("Register")
                  }
                )
              ] }) : null,
              shouldDisplayAccountDropdown ? /* @__PURE__ */ jsx(
                AccountDropdown,
                {
                  id,
                  onToggleDropdown: onToggleButtonClick,
                  linkRenderer: LinkRenderer,
                  language
                }
              ) : null
            ] })
          }
        ),
        shouldDisplayCtaButton ? /* @__PURE__ */ jsx(
          ButtonLink,
          {
            variant: "ghost",
            tone: "neutral",
            size: "small",
            href: ctaURL,
            data: {
              analytics: analyticsEvents.TRACK_CREATE_JOB,
              test: testTags.CREATE_A_JOB_AD
            },
            onClick: () => {
              handleInteractionTracking(
                analyticsDesktopHeaderEvents.createAJobAd
              );
            },
            children: t("Create a job ad")
          }
        ) : null
      ] })
    }
  );
};
export {
  HeaderActions,
  getLanguagesForSelector,
  languageMapping
};
